import { AfterViewInit, Component, EventEmitter, OnInit } from '@angular/core';
import { SavedDashboardViewHelper } from 'src/app/components/dashboard/saved-dashboard-view/saved-dashboard-view-helper';
import { ISavedDashboardViewComponentOptions } from 'src/app/components/dashboard/saved-dashboard-view/saved-dashboard-view.component';
import { TableDashboardItemHelper } from 'src/app/components/table-dashboard-item/table-dashboard-item-helper';
import { TableColumnItem } from 'src/app/components/table-dashboard-item/table-dashboard-item.component';
import { CommonLegendService } from 'src/app/services/common-legend.service';
import { IDashboardItemDataLoaderLink } from 'src/app/services/dashboard-item/IDashboardItemDataLoaderLink';
import {
    DashboardService,
    IDashboardInitOptions,
} from 'src/app/services/dashboard/dashboard.service';
import { IDataLoaderResult } from 'src/app/services/data-loader/data-loader-result';
import { DataLoaderFactories } from 'src/app/services/data-loader/data-loader.service';
import {
    IQueryContext,
    QueryContextDefaultSettings,
    QueryContextSettingID,
} from 'src/app/services/query-context/models/IQueryContext';
import { DashboardView } from 'src/app/services/query-context/models/dashboard-view.model';
import { QueryContextService } from 'src/app/services/query-context/query-context.service';
import {
    DatasourceColumnId,
    datasourceColumnOrderByQuery,
} from 'src/app/utils/datasource-sql-map/datasource';
import { EChartsAxisHelper } from 'src/app/utils/echarts/echarts-axis-helper';
import { EChartsHelper } from 'src/app/utils/echarts/echarts-helper';
import { EChartsTooltipHelper } from 'src/app/utils/echarts/echarts-tooltip-helper';
import { defaultColorGetterBuilder } from 'src/app/utils/echarts/series-color';
import { GcpProjectId } from 'src/app/utils/gcp-project-id.model';
import { currentDataProject } from 'src/app/services/data-project/models/data-project.model';
import { SegmentDatasourceColumnDataLoaderFactoryOptions } from '../common/data-loaders/segments';
import { DashboardItemFactories } from '../common/items/common-dashboard-items';
import { ModifyTimeDashboardItemFactoryOptions } from '../common/items/modify-time';
import { ICheckboxSettingDashboardItem } from '../common/items/setting/checkbox/checkbox.component';
import { IStringInputQueryContextSettingDashboardItem as IStringInputQueryContextSettingDashboardItemOptions } from '../common/items/setting/string-input-setting/string-input-setting.component';
import { IToggleSettingDashboardItem } from '../common/items/setting/toggle/toggle.component';
import { RetentionByDayNoDataLoaderFactory } from '../common/retention/data-loaders/retention-by-day-no';
import { StandardDashboardWorker } from '../common/standard-dashboard';
import { EngagementSessionsByDayNoDataLoaderFactory } from '../mopa-engagement/data-loaders/engagement-sessions-by-day-no';
import {
    RevenueARPUDataLoaderDocumentation,
    RevenueARPUDataLoaderFactory,
} from '../mopa-sales/data-loaders/revenue-arpu';
import { RevenueDauDataLoaderFactory } from '../mopa-sales/data-loaders/revenue-dau';
import { BIVizDashboardSales } from '../mopa-sales/sql-maps/biviz-dashboard-sales-datasource-sql-map';
import { MopaAbtestsChartDocs } from './mopa-abtests.chart-documentation';
import {
    AbTestsConversionRateDataLoaderDocumentation,
    AbTestsConversionRateDataLoaderFactory,
} from './data-loaders/AbTestsConversionRate';
import { AbTestsProgressSessionDataLoaderFactory } from './data-loaders/AbTestsProgressSession';

@Component({
    selector: 'app-mopa-abtests',
    templateUrl: './mopa-abtests.component.html',
    styleUrls: ['./mopa-abtests.component.scss'],
    providers: [DashboardService, QueryContextService, CommonLegendService],
})
export class MopaAbtestsComponent
    extends StandardDashboardWorker
    implements OnInit, AfterViewInit
{
    override DASHBOARD_NAME = 'A/B Tests';
    override DASHBOARD_ID = 'mopa-abtests';
    override dashboardInitOptions: IDashboardInitOptions = {
        savedDashboardViewsCompareFn: (a, b) =>
            SavedDashboardViewHelper.compareDashboardViewsByName(a, b, 'desc'),
        selectInitialDashboardViewClb:
            MopaAbtestsComponent.getDefaultABTestDashboardView,
    };

    private gcpProjectId: GcpProjectId = currentDataProject().gcpProjectId;
    public savedDashboardViewComponentOptions: ISavedDashboardViewComponentOptions =
        {
            showNavigationButtons: true,
        };

    /////////////////////////////////////////////////////////////////
    ////
    ////    DATALOADERS
    ////    Some DataLoader can be used in multiple charts.
    ////    That's why there are created here and later referenced
    ////
    /////////////////////////////////////////////////////////////////

    /////////////////////////////////////////////////////////////////
    ////
    ////    FILTERS DATALOADERS
    ////
    /////////////////////////////////////////////////////////////////

    override filterDataLoaders = {
        'mopa.abtests.filter.inapp_source': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.abtests.filter.inapp_source',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'SALES_INAPP_SOURCE',
            datasourceSqlMap: BIVizDashboardSales.sqlMap,
            filterContext: true,
        }).create(),
        'mopa.abtests.filter.offer_name': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.abtests.filter.offer_name',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'SALES_OFFER_NAME',
            datasourceSqlMap: BIVizDashboardSales.sqlMap,
            filterContext: true,
        }).create(),
        'mopa.abtests.filter.offer_type': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.abtests.filter.offer_type',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'SALES_OFFER_TYPE',
            datasourceSqlMap: BIVizDashboardSales.sqlMap,
            filterContext: true,
        }).create(),
        'mopa.abtests.filter.offer_segment': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.abtests.filter.offer_segment',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'SALES_OFFER_SEGMENT',
            datasourceSqlMap: BIVizDashboardSales.sqlMap,
            filterContext: true,
        }).create(),
        'mopa.abtests.filter.offer_season_variant': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.abtests.filter.offer_season_variant',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'SALES_OFFER_SEASON_VARIANT',
            datasourceSqlMap: BIVizDashboardSales.sqlMap,
            filterContext: true,
            orderByQuery: datasourceColumnOrderByQuery(
                'SALES_OFFER_SEASON_VARIANT'
            ),
        }).create(),
        'mopa.abtests.filter.offer_season_size': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.abtests.filter.offer_season_size',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'SALES_OFFER_SEASON_SIZE',
            datasourceSqlMap: BIVizDashboardSales.sqlMap,
            filterContext: true,
            orderByQuery: datasourceColumnOrderByQuery(
                'SALES_OFFER_SEASON_SIZE'
            ),
        }).create(),
        'mopa.abtests.filter.inapp_product_id': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.abtests.filter.inapp_product_id',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'SALES_INAPP_PRODUCT_ID',
            datasourceSqlMap: BIVizDashboardSales.sqlMap,
            filterContext: true,
        }).create(),
        'mopa.abtests.filter.inapp_product_id_price_tier': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId:
                'mopa.abtests.filter.inapp_product_id_price_tier',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'SALES_INAPP_PRODUCT_ID_PRICE_TIER',
            datasourceSqlMap: BIVizDashboardSales.sqlMap,
            filterContext: true,
            orderByQuery: datasourceColumnOrderByQuery(
                'SALES_INAPP_PRODUCT_ID_PRICE_TIER'
            ),
        }).create(),
        'mopa.abtests.filter.segment': DataLoaderFactories[
            'common.filter.segment'
        ](
            new SegmentDatasourceColumnDataLoaderFactoryOptions(
                'mopa.abtests.filter_segments',
                this.gcpProjectId,
                this.queryCache
            )
        ).create(),
        'mopa.abtests.filter.segment_exclude': DataLoaderFactories[
            'common.filter.segment'
        ](
            new SegmentDatasourceColumnDataLoaderFactoryOptions(
                'mopa.abtests.filter_segments_exclude',
                this.gcpProjectId,
                this.queryCache,
                'SEGMENT_EXCLUDE'
            )
        ).create(),
        'mopa.abtests.filter.firebase_app_store': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.abtests.filter.firebase_app_store',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'FIREBASE_APP_STORE',
            datasourceSqlMap: BIVizDashboardSales.sqlMap,
        }).create(),
        'mopa.abtests.filter.firebase_country_european_union':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'mopa.abtests.filter.firebase_country_european_union',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'FIREBASE_COUNTRY_EUROPEAN_UNION',
                datasourceSqlMap: BIVizDashboardSales.sqlMap,
            }).create(),
        'mopa.abtests.filter.firebase_country_tier': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.abtests.filter.firebase_country_tier',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'FIREBASE_COUNTRY_TIER',
            datasourceSqlMap: BIVizDashboardSales.sqlMap,
            // filterContext: ['FIREBASE_COUNTRY'],
        }).create(),
        'mopa.abtests.filter.firebase_country': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.abtests.filter.firebase_country',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'FIREBASE_COUNTRY',
            datasourceSqlMap: BIVizDashboardSales.sqlMap,
            filterContext: ['FIREBASE_COUNTRY_TIER'],
        }).create(),
        'mopa.abtests.filter.attribution_traffic_source': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.abtests.filter.attribution_traffic_source',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'ATTRIBUTION_TRAFFIC_SOURCE',
            datasourceSqlMap: BIVizDashboardSales.sqlMap,
        }).create(),
        'mopa.abtests.filter.firebase_install_version': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.abtests.filter.firebase_install_version',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'FIREBASE_INSTALL_VERSION',
            datasourceSqlMap: BIVizDashboardSales.sqlMap,
            filterContext: true,
            orderByQuery: datasourceColumnOrderByQuery(
                'FIREBASE_INSTALL_VERSION'
            ),
        }).create(),
        'mopa.abtests.filter.firebase_install_version_group_minor':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'mopa.abtests.filter.firebase_install_version_group_minor',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'FIREBASE_INSTALL_VERSION_GROUP_MINOR',
                datasourceSqlMap: BIVizDashboardSales.sqlMap,
                filterContext: true,
                orderByQuery: datasourceColumnOrderByQuery(
                    'FIREBASE_INSTALL_VERSION_GROUP_MINOR'
                ),
            }).create(),
        'mopa.abtests.filter.etl_payer': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.abtests.filter.etl_payer',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'ETL_PAYER',
            datasourceSqlMap: BIVizDashboardSales.sqlMap,
        }).create(),
        'mopa.abtests.filter.etl_revenue_badge': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.abtests.filter.etl_revenue_badge',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'ETL_REVENUE_BADGE',
            datasourceSqlMap: BIVizDashboardSales.sqlMap,
            orderByQuery: datasourceColumnOrderByQuery('ETL_REVENUE_BADGE'),
        }).create(),
        'mopa.abtests.filter.attribution_network_name': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.abtests.filter.attribution_network_name',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'ATTRIBUTION_NETWORK_NAME',
            datasourceSqlMap: BIVizDashboardSales.sqlMap,
        }).create(),
        'mopa.abtests.filter.attribution_campaign_name': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.abtests.filter.attribution_campaign_name',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'ATTRIBUTION_CAMPAIGN_NAME',
            datasourceSqlMap: BIVizDashboardSales.sqlMap,
            filterContext: true,
        }).create(),
    };

    /////////////////////////////////////////////////////////////////
    ////
    ////    CHART DATALOADERS
    ////
    /////////////////////////////////////////////////////////////////

    override chartDataLoaders = {
        'mopa.abtests.modify_time_datasource': DataLoaderFactories[
            'common.modify_time'
        ]({
            createdInstanceId: 'mopa.abtests.modify_time_users_datasource',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasetId: BIVizDashboardSales.sqlMap.dataset,
            tableId: BIVizDashboardSales.sqlMap.tableId,
        }).create(),
        // 'mopa.abtests.cohorts_overview_table':
        //     new AbTestsCohortsOverviewTableDataLoaderFactory({
        //         createdInstanceId: 'mopa.abtests.cohorts_overview_table',
        //         gcpProjectId: this.defaultGcpProjectId,
        //         queryCacheService: this.queryCache,
        //     }).create(),
        'mopa.abtests.revenue_arpu': new RevenueARPUDataLoaderFactory({
            createdInstanceId: 'mopa.abtests.revenue_arpu',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            maxDayNoGetter(context) {
                return (
                    context.settings[
                        QueryContextSettingID.ABTESTS_MAX_DAY_NO
                    ] ??
                    QueryContextDefaultSettings[
                        QueryContextSettingID.ABTESTS_MAX_DAY_NO
                    ]!
                );
            },
            calculateUplift: {
                upliftMetrics: [
                    'ARPU_net_pln',
                    'ARPU_net_usd',
                    'ARPU_gross_pln',
                    'ARPU_gross_usd',
                    'inapp_ARPU_net_pln',
                    'inapp_ARPU_net_usd',
                    'inapp_ARPU_gross_pln',
                    'inapp_ARPU_gross_usd',
                    'ad_ARPU_net_pln',
                    'ad_ARPU_net_usd',
                    'ad_ARPU_gross_pln',
                    'ad_ARPU_gross_usd',
                    'ARPU_daily_net_pln',
                    'ARPU_daily_net_usd',
                    'ARPU_daily_gross_pln',
                    'ARPU_daily_gross_usd',
                    'inapp_ARPU_daily_net_pln',
                    'inapp_ARPU_daily_net_usd',
                    'inapp_ARPU_daily_gross_pln',
                    'inapp_ARPU_daily_gross_usd',
                    'ad_ARPU_daily_net_pln',
                    'ad_ARPU_daily_net_usd',
                    'ad_ARPU_daily_gross_pln',
                    'ad_ARPU_daily_gross_usd',
                    'running_revenue_net_pln',
                    'running_revenue_net_usd',
                    'running_revenue_gross_pln',
                    'running_revenue_gross_usd',
                    'inapp_running_revenue_net_pln',
                    'inapp_running_revenue_net_usd',
                    'inapp_running_revenue_gross_pln',
                    'inapp_running_revenue_gross_usd',
                    'ad_running_revenue_net_pln',
                    'ad_running_revenue_net_usd',
                    'ad_running_revenue_gross_pln',
                    'ad_running_revenue_gross_usd',
                ],
                upliftReferenceSegmentSettingId:
                    QueryContextSettingID.ABTESTS_UPLIFT_REFERENCE,
            },
        }).create(),
        'mopa.abtests.conversion_rate':
            new AbTestsConversionRateDataLoaderFactory({
                createdInstanceId: 'mopa.abtests.conversion_rate',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                maxDayNoGetter(context) {
                    return (
                        context.settings[
                            QueryContextSettingID.ABTESTS_MAX_DAY_NO
                        ] ??
                        QueryContextDefaultSettings[
                            QueryContextSettingID.ABTESTS_MAX_DAY_NO
                        ]!
                    );
                },
                calculateUplift: {
                    upliftMetrics: ['running_cr', 'purchase_rate'],
                    upliftReferenceSegmentSettingId:
                        QueryContextSettingID.ABTESTS_UPLIFT_REFERENCE,
                },
            }).create(),
        'mopa.abtests.progress_sessions':
            new AbTestsProgressSessionDataLoaderFactory({
                createdInstanceId: 'mopa.abtests.progress_sessions',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                maxDayNoGetter(context) {
                    return (
                        context.settings[
                            QueryContextSettingID.ABTESTS_MAX_DAY_NO
                        ] ??
                        QueryContextDefaultSettings[
                            QueryContextSettingID.ABTESTS_MAX_DAY_NO
                        ]!
                    );
                },
                calculateUplift: {
                    upliftMetrics: [
                        'median_progress_sum_total',
                        'running_median_duration_min',
                        'players',
                    ],
                    upliftReferenceSegmentSettingId:
                        QueryContextSettingID.ABTESTS_UPLIFT_REFERENCE,
                },
            }).create(),
        'mopa.abtests.retention_by_day_no':
            new RetentionByDayNoDataLoaderFactory({
                createdInstanceId: 'mopa.abtests.retention_by_day_no',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                maxDayNoGetter(context) {
                    return (
                        context.settings[
                            QueryContextSettingID.ABTESTS_MAX_DAY_NO
                        ] ??
                        QueryContextDefaultSettings[
                            QueryContextSettingID.ABTESTS_MAX_DAY_NO
                        ]!
                    );
                },
                calculateUplift: {
                    upliftMetrics: ['avg_user_lifetime'],
                    upliftReferenceSegmentSettingId:
                        QueryContextSettingID.ABTESTS_UPLIFT_REFERENCE,
                },
            }).create(),
        'mopa.abtests.sessions_by_day_no':
            new EngagementSessionsByDayNoDataLoaderFactory({
                createdInstanceId: 'mopa.abtests.sessions_by_day_no',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                maxDayNoGetter(context) {
                    return (
                        context.settings[
                            QueryContextSettingID.ABTESTS_MAX_DAY_NO
                        ] ??
                        QueryContextDefaultSettings[
                            QueryContextSettingID.ABTESTS_MAX_DAY_NO
                        ]!
                    );
                },
                calculateUplift: {
                    upliftMetrics: [
                        'median_sess_length_mins',
                        'avg_sess_per_user',
                    ],
                    upliftReferenceSegmentSettingId:
                        QueryContextSettingID.ABTESTS_UPLIFT_REFERENCE,
                },
            }).create(),
        'mopa.abtests.revenue_agg_dau': new RevenueDauDataLoaderFactory({
            createdInstanceId: 'mopa.abtests.revenue_agg_dau',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
        }).create(),
    };

    /////////////////////////////////////////////////////////////////
    ////
    ////    END OF DATALOADERS
    ////
    /////////////////////////////////////////////////////////////////

    /////////////////////////////////////////////////////////////////
    ////
    ////    [DASHBOARD ITEM <-> DATALOADER] LINKS
    ////
    /////////////////////////////////////////////////////////////////

    // SETTINGS
    settingMaxDayNoOptions: IStringInputQueryContextSettingDashboardItemOptions =
        {
            id: QueryContextSettingID.ABTESTS_MAX_DAY_NO,
            viewtype: 'string-input',
            displayName: 'Limit max day no',
            value: this.getInitialContext().settings[
                QueryContextSettingID.ABTESTS_MAX_DAY_NO
            ],
        };
    settingItemNetGross: IToggleSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.NET_GROSS,
            viewtype: 'toggle',
            value: 'net',
            selectOptions: ['net', 'gross'],
        },
    };
    settingItemCurrency: IToggleSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.CURRENCY,
            viewtype: 'toggle',
            value: 'pln',
            selectOptions: ['pln', 'usd'],
        },
    };
    settingItemShowTotalRevenue: ICheckboxSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.SHOW_TOTAL_REVENUE,
            viewtype: 'checkbox',
            displayName: 'total',
            value: true,
        },
    };
    settingItemShowInappRevenue: ICheckboxSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.SHOW_INAPP_REVENUE,
            viewtype: 'checkbox',
            displayName: 'inapp',
        },
    };
    settingItemShowAdRevenue: ICheckboxSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.SHOW_AD_REVENUE,
            viewtype: 'checkbox',
            displayName: 'ad',
        },
    };
    // FILTERS
    filterDashboardItemLinks: Partial<
        Record<DatasourceColumnId, IDashboardItemDataLoaderLink>
    > = {
        SALES_OFFER_TYPE: {
            id: 'mopa.abtests.filter.sales_offer_type',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId: 'mopa.abtests.filter.sales_offer_type',
                datasourceColumnId: 'SALES_OFFER_TYPE',
            }),
            dataLoaderEmitter:
                this.filterDataLoaders['mopa.abtests.filter.offer_type']
                    .emitter,
        },
        SALES_OFFER_SEGMENT: {
            id: 'mopa.abtests.filter.sales_offer_segment',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId: 'mopa.abtests.filter.sales_offer_segment',
                datasourceColumnId: 'SALES_OFFER_SEGMENT',
            }),
            dataLoaderEmitter:
                this.filterDataLoaders['mopa.abtests.filter.offer_segment']
                    .emitter,
        },
        SALES_OFFER_SEASON_SIZE: {
            id: 'mopa.abtests.filter.sales_offer_season_size',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId:
                    'mopa.abtests.filter.sales_offer_season_size',
                datasourceColumnId: 'SALES_OFFER_SEASON_SIZE',
            }),
            dataLoaderEmitter:
                this.filterDataLoaders['mopa.abtests.filter.offer_season_size']
                    .emitter,
        },
        SALES_OFFER_SEASON_VARIANT: {
            id: 'mopa.abtests.filter.sales_offer_season_variant',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId:
                    'mopa.abtests.filter.sales_offer_season_variant',
                datasourceColumnId: 'SALES_OFFER_SEASON_VARIANT',
            }),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.abtests.filter.offer_season_variant'
                ].emitter,
        },
        SALES_OFFER_NAME: {
            id: 'mopa.abtests.filter.sales_offer_name',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId: 'mopa.abtests.filter.sales_offer_name',
                datasourceColumnId: 'SALES_OFFER_NAME',
            }),
            dataLoaderEmitter:
                this.filterDataLoaders['mopa.abtests.filter.offer_name']
                    .emitter,
        },
        SALES_INAPP_SOURCE: {
            id: 'mopa.abtests.filter.sales_inapp_source',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId: 'mopa.abtests.filter.sales_inapp_source',
                datasourceColumnId: 'SALES_INAPP_SOURCE',
            }),
            dataLoaderEmitter:
                this.filterDataLoaders['mopa.abtests.filter.inapp_source']
                    .emitter,
        },
        SALES_INAPP_PRODUCT_ID: {
            id: 'mopa.abtests.filter.sales_inapp_product_id',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId: 'mopa.abtests.filter.sales_inapp_product_id',
                datasourceColumnId: 'SALES_INAPP_PRODUCT_ID',
            }),
            dataLoaderEmitter:
                this.filterDataLoaders['mopa.abtests.filter.inapp_product_id']
                    .emitter,
        },
        SALES_INAPP_PRODUCT_ID_PRICE_TIER: {
            id: 'mopa.abtests.filter.sales_inapp_product_id_price_tier',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId:
                    'mopa.abtests.filter.sales_inapp_product_id_price_tier',
                datasourceColumnId: 'SALES_INAPP_PRODUCT_ID_PRICE_TIER',
            }),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.abtests.filter.inapp_product_id_price_tier'
                ].emitter,
        },

        SEGMENT: {
            id: 'mopa.abtests.filter.segments',
            dashboardItemFactory: DashboardItemFactories['filter.segments']({
                createdInstanceId: 'mopa.abtests.filter.segments',
                upliftReferenceSegmentSettingId:
                    QueryContextSettingID.ABTESTS_UPLIFT_REFERENCE,
            }),
            dataLoaderEmitter:
                this.filterDataLoaders['mopa.abtests.filter.segment'].emitter,
        },
        SEGMENT_EXCLUDE: {
            id: 'mopa.abtests.filter.segments_exclude',
            dashboardItemFactory: DashboardItemFactories['filter.segments']({
                createdInstanceId: 'mopa.abtests.filter.segments_exclude',
                datasourceColumnId: 'SEGMENT_EXCLUDE',
                excludeMode: true,
                showNewSegmentLink: false,
            }),
            dataLoaderEmitter:
                this.filterDataLoaders['mopa.abtests.filter.segment_exclude']
                    .emitter,
        },
        FIREBASE_FIRST_OPEN_DATE: {
            id: 'mopa.abtests.filter.firebase_first_open_date',
            dashboardItemFactory: DashboardItemFactories['filter.daterange']({
                createdInstanceId:
                    'mopa.abtests.filter.firebase_first_open_date',
                datasourceColumnId: 'FIREBASE_FIRST_OPEN_DATE',
                splitable: true,
            }),
            // empty emitter DataRangePicker doesn't need data
            dataLoaderEmitter: new EventEmitter<IDataLoaderResult>(),
        },
        FIREBASE_APP_STORE: {
            id: 'mopa.abtests.filter.firebase_app_store',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId: 'mopa.abtests.filter.firebase_app_store',
                datasourceColumnId: 'FIREBASE_APP_STORE',
            }),
            dataLoaderEmitter:
                this.filterDataLoaders['mopa.abtests.filter.firebase_app_store']
                    .emitter,
        },
        FIREBASE_COUNTRY_EUROPEAN_UNION: {
            id: 'mopa.abtests.filter.firebase_country_european_union',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId:
                    'mopa.abtests.filter.firebase_country_european_union',
                datasourceColumnId: 'FIREBASE_COUNTRY_EUROPEAN_UNION',
            }),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.abtests.filter.firebase_country_european_union'
                ].emitter,
        },
        FIREBASE_COUNTRY_TIER: {
            id: 'mopa.abtests.filter.firebase_country_tier',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId: 'mopa.abtests.filter.firebase_country_tier',
                datasourceColumnId: 'FIREBASE_COUNTRY_TIER',
            }),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.abtests.filter.firebase_country_tier'
                ].emitter,
        },
        FIREBASE_COUNTRY: {
            id: 'mopa.abtests.filter.firebase_country',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId: 'mopa.abtests.filter.firebase_country',
                datasourceColumnId: 'FIREBASE_COUNTRY',
            }),
            dataLoaderEmitter:
                this.filterDataLoaders['mopa.abtests.filter.firebase_country']
                    .emitter,
        },
        ATTRIBUTION_TRAFFIC_SOURCE: {
            id: 'mopa.abtests.filter.attribution_traffic_source',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId:
                    'mopa.abtests.filter.attribution_traffic_source',
                datasourceColumnId: 'ATTRIBUTION_TRAFFIC_SOURCE',
            }),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.abtests.filter.attribution_traffic_source'
                ].emitter,
        },
        FIREBASE_INSTALL_VERSION_GROUP_MINOR: {
            id: 'mopa.abtests.filter.firebase_install_version_group_minor',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId:
                    'mopa.abtests.filter.firebase_install_version_group_minor',
                datasourceColumnId: 'FIREBASE_INSTALL_VERSION_GROUP_MINOR',
            }),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.abtests.filter.firebase_install_version_group_minor'
                ].emitter,
        },
        FIREBASE_INSTALL_VERSION: {
            id: 'mopa.abtests.filter.firebase_install_version',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId:
                    'mopa.abtests.filter.firebase_install_version',
                datasourceColumnId: 'FIREBASE_INSTALL_VERSION',
            }),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.abtests.filter.firebase_install_version'
                ].emitter,
        },
        ETL_PAYER: {
            id: 'mopa.abtests.filter.etl_payer',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId: 'mopa.abtests.filter.etl_payer',
                datasourceColumnId: 'ETL_PAYER',
            }),
            dataLoaderEmitter:
                this.filterDataLoaders['mopa.abtests.filter.etl_payer'].emitter,
        },
        ETL_REVENUE_BADGE: {
            id: 'mopa.abtests.filter.etl_revenue_badge',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId: 'mopa.abtests.filter.etl_revenue_badge',
                datasourceColumnId: 'ETL_REVENUE_BADGE',
            }),
            dataLoaderEmitter:
                this.filterDataLoaders['mopa.abtests.filter.etl_revenue_badge']
                    .emitter,
        },
        ATTRIBUTION_NETWORK_NAME: {
            id: 'mopa.abtests.filter.attribution_network_name',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId:
                    'mopa.abtests.filter.attribution_network_name',
                datasourceColumnId: 'ATTRIBUTION_NETWORK_NAME',
            }),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.abtests.filter.attribution_network_name'
                ].emitter,
        },
        ATTRIBUTION_CAMPAIGN_NAME: {
            id: 'mopa.abtests.filter.attribution_campaign_name',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId:
                    'mopa.abtests.filter.attribution_campaign_name',
                datasourceColumnId: 'ATTRIBUTION_CAMPAIGN_NAME',
            }),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.abtests.filter.attribution_campaign_name'
                ].emitter,
        },
    };

    // CHARTS
    modify_time_users_datasource: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.abtests.modify_time_users_datasource',
        dashboardItemFactory: DashboardItemFactories['common.modify_time'](
            new ModifyTimeDashboardItemFactoryOptions(
                'mopa.abtests.modify_time_users_datasource',
                'value'
            )
        ),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.abtests.modify_time_datasource']
                .emitter,
    };

    // table_summary = {
    //     id: 'lnk.mopa.abtests.cohorts_overview_table',
    //     dashboardItem: TableDashboardItemHelper.getTableDashboardItem({
    //         dashboardName: this.DASHBOARD_NAME,
    //         instanceId: 'mopa.devices.cohorts_overview_table',
    //         dashboardItem: {
    //             title: 'Cohort overview',
    //         },
    //         columnsGetter:
    //             MopaAbtestsComponent.cohortOverviewTableColumnsGetter,
    //         nzPageSize: 10,
    //         // nzTableLayout: 'auto',
    //     }),
    //     dataLoaderEmitter:
    //         this.chartDataLoaders['mopa.abtests.cohorts_overview_table']
    //             .emitter,
    // };

    arpu: IDashboardItemDataLoaderLink = {
        id: 'mopa.abtests.chart.arpu',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            createdInstanceId: 'mopa.abtests.chart.arpu',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            dashboardItem: {
                title: 'Running ARPU',
                chartDocumentation:
                    RevenueARPUDataLoaderDocumentation.metrics.running_ARPU
                        .chartDocumentation,
            },
            dashboardName: this.DASHBOARD_NAME,
            metricsGetter: EChartsHelper.createRevenueMetricsGetter({
                metricId: 'ARPU',
                metricName: 'Running ARPU',
            }),
            xAxisType: 'value',
            xColumnLabel: 'day_no',
            echartsSeriesType: 'line',
            tooltip: {
                xLabelFormatterGetter: () => (value: string) =>
                    `Day no: ${value}`,
                valueFormatterGetter: (context: IQueryContext) => (v) =>
                    EChartsTooltipHelper.tooltipCurrencyValueFormatter(
                        v,
                        context.settings.currency || 'pln'
                    ),
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.abtests.revenue_arpu'].emitter,
    };

    // arpu uplift
    arpu_uplift: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.abtests.arpu_uplift',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            createdInstanceId: 'mopa.abtests.chart.arpu_uplift',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            dashboardItem: {
                title: 'Running ARPU uplift',
                chartDocumentation: MopaAbtestsChartDocs.arpu_uplift,
            },
            dashboardName: this.DASHBOARD_NAME,
            metricsGetter: EChartsHelper.createRevenueMetricsGetter({
                metricId: 'ARPU',
                metricName: 'ARPU uplift',
                metricSuffix: 'uplift',
            }),
            xAxisType: 'value',
            xColumnLabel: 'day_no',
            echartsSeriesType: 'line',
            yAxisLabel: {
                formatter: EChartsAxisHelper.getAxisPercentLabelFormatter(0),
            },
            tooltip: {
                xLabelFormatterGetter: () => (value: string) =>
                    `Day no: ${value}`,
                valueFormatterGetter: () =>
                    EChartsTooltipHelper.tooltipPercentValueFormatter,
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.abtests.revenue_arpu'].emitter,
    };

    arpu_daily: IDashboardItemDataLoaderLink = {
        id: 'mopa.abtests.chart.arpu_daily',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            createdInstanceId: 'mopa.abtests.chart.arpu_daily',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            dashboardItem: {
                title: 'ARPU daily',
                chartDocumentation:
                    RevenueARPUDataLoaderDocumentation.metrics.ARPU_daily
                        .chartDocumentation,
            },
            dashboardName: this.DASHBOARD_NAME,
            metricsGetter: EChartsHelper.createRevenueMetricsGetter({
                metricId: 'ARPU_daily',
                metricName: 'ARPU_daily',
            }),
            xAxisType: 'value',
            xColumnLabel: 'day_no',
            echartsSeriesType: 'line',
            tooltip: {
                xLabelFormatterGetter: () => (value: string) =>
                    `Day no: ${value}`,
                valueFormatterGetter: (context: IQueryContext) => (v) =>
                    EChartsTooltipHelper.tooltipCurrencyValueFormatter(
                        v,
                        context.settings.currency || 'pln'
                    ),
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.abtests.revenue_arpu'].emitter,
    };

    arpu_daily_uplift: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.abtests.arpu_daily_uplift',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            createdInstanceId: 'mopa.abtests.chart.arpu_daily_uplift',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            dashboardItem: {
                title: 'ARPU daily uplift',
                chartDocumentation: MopaAbtestsChartDocs.arpu_daily_uplift,
            },
            dashboardName: this.DASHBOARD_NAME,
            metricsGetter: EChartsHelper.createRevenueMetricsGetter({
                metricId: 'ARPU_daily',
                metricName: 'ARPU daily uplift',
                metricSuffix: 'uplift',
            }),
            xAxisType: 'value',
            xColumnLabel: 'day_no',
            echartsSeriesType: 'line',
            yAxisLabel: {
                formatter: EChartsAxisHelper.getAxisPercentLabelFormatter(0),
            },
            tooltip: {
                xLabelFormatterGetter: () => (value: string) =>
                    `Day no: ${value}`,
                valueFormatterGetter: () =>
                    EChartsTooltipHelper.tooltipPercentValueFormatter,
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.abtests.revenue_arpu'].emitter,
    };

    running_revenue: IDashboardItemDataLoaderLink = {
        id: 'mopa.abtests.chart.running_revenue',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            createdInstanceId: 'mopa.abtests.chart.running_revenue',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            dashboardItem: {
                title: 'Running cohort revenue',
                chartDocumentation:
                    RevenueARPUDataLoaderDocumentation.metrics.running_revenue
                        .chartDocumentation,
            },
            dashboardName: this.DASHBOARD_NAME,
            metricsGetter: EChartsHelper.createRevenueMetricsGetter({
                metricId: 'running_revenue',
                metricName: 'Running cohort revenue',
            }),
            xAxisType: 'value',
            xColumnLabel: 'day_no',
            echartsSeriesType: 'line',
            tooltip: {
                xLabelFormatterGetter: () => (value: string) =>
                    `Day no: ${value}`,
                valueFormatterGetter: (context: IQueryContext) => (v) =>
                    EChartsTooltipHelper.tooltipCurrencyValueFormatter(
                        v,
                        context.settings.currency || 'pln'
                    ),
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.abtests.revenue_arpu'].emitter,
    };

    // running cohort revenue uplift
    running_revenue_uplift: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.abtests.running_revenue_uplift',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            createdInstanceId: 'mopa.abtests.chart.running_revenue_uplift',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            dashboardItem: {
                title: 'Running cohort revenue uplift',
                chartDocumentation: MopaAbtestsChartDocs.running_revenue_uplift,
            },
            dashboardName: this.DASHBOARD_NAME,
            metricsGetter: EChartsHelper.createRevenueMetricsGetter({
                metricId: 'running_revenue',
                metricName: 'Running cohort revenue uplift',
                metricSuffix: 'uplift',
            }),
            xAxisType: 'value',
            xColumnLabel: 'day_no',
            echartsSeriesType: 'line',
            yAxisLabel: {
                formatter: EChartsAxisHelper.getAxisPercentLabelFormatter(0),
            },
            tooltip: {
                xLabelFormatterGetter: () => (value: string) =>
                    `Day no: ${value}`,
                valueFormatterGetter: () =>
                    EChartsTooltipHelper.tooltipPercentValueFormatter,
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.abtests.revenue_arpu'].emitter,
    };

    running_cr: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.retention.chart.running_cr',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId: 'mopa.abtests.chart.running_cr',
            dashboardItem: {
                title: 'Running CR',
                chartDocumentation:
                    AbTestsConversionRateDataLoaderDocumentation.metrics
                        .running_cr.chartDocumentation,
            },
            echartsSeriesType: 'line',
            xColumnLabel: 'day_no',
            yColumnLabel: 'running_cr',
            metricName: 'Running CR',
            xAxisType: 'value',
            yAxisLabel: {
                formatter: EChartsAxisHelper.getAxisPercentLabelFormatter(0),
            },
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            tooltip: {
                xLabelFormatterGetter: () => (value: string) =>
                    `Day no: ${value}`,
                valueFormatterGetter: () =>
                    EChartsTooltipHelper.tooltipPercentValueFormatter,
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.abtests.conversion_rate'].emitter,
    };

    running_cr_uplift: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.retention.chart.running_cr_uplift',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId: 'mopa.abtests.chart.running_cr_uplift',
            dashboardItem: {
                title: 'Running CR uplift',
                chartDocumentation: MopaAbtestsChartDocs.running_cr_uplift,
            },
            echartsSeriesType: 'line',
            xColumnLabel: 'day_no',
            yColumnLabel: 'running_cr_uplift',
            metricName: 'Running CR uplift',
            xAxisType: 'value',
            yAxisLabel: {
                formatter: EChartsAxisHelper.getAxisPercentLabelFormatter(0),
            },
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            tooltip: {
                xLabelFormatterGetter: () => (value: string) =>
                    `Day no: ${value}`,
                valueFormatterGetter: () =>
                    EChartsTooltipHelper.tooltipPercentValueFormatter,
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.abtests.conversion_rate'].emitter,
    };

    purchase_rate: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.retention.chart.purchase_rate',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId: 'mopa.abtests.chart.purchase_rate',
            dashboardItem: {
                title: 'Purchase rate',
                chartDocumentation:
                    AbTestsConversionRateDataLoaderDocumentation.metrics
                        .purchase_rate.chartDocumentation,
            },
            echartsSeriesType: 'line',
            xColumnLabel: 'day_no',
            yColumnLabel: 'purchase_rate',
            metricName: 'Purchase rate',
            xAxisType: 'value',
            yAxisLabel: {
                formatter: EChartsAxisHelper.getAxisPercentLabelFormatter(0),
            },
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            tooltip: {
                xLabelFormatterGetter: () => (value: string) =>
                    `Day no: ${value}`,
                valueFormatterGetter: () =>
                    EChartsTooltipHelper.tooltipPercentValueFormatter,
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.abtests.conversion_rate'].emitter,
    };

    purchase_rate_uplift: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.retention.chart.purchase_rate_uplift',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId: 'mopa.abtests.chart.purchase_rate_uplift',
            dashboardItem: {
                title: 'Purchase rate uplift',
                chartDocumentation: MopaAbtestsChartDocs.purchase_rate_uplift,
            },
            echartsSeriesType: 'line',
            xColumnLabel: 'day_no',
            yColumnLabel: 'purchase_rate_uplift',
            metricName: 'Purchase rate uplift',
            xAxisType: 'value',
            yAxisLabel: {
                formatter: EChartsAxisHelper.getAxisPercentLabelFormatter(0),
            },
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            tooltip: {
                xLabelFormatterGetter: () => (value: string) =>
                    `Day no: ${value}`,
                valueFormatterGetter: () =>
                    EChartsTooltipHelper.tooltipPercentValueFormatter,
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.abtests.conversion_rate'].emitter,
    };

    avg_user_lifetime: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.retention.chart.avg_user_lifetime',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId: 'mopa.retention.chart.avg_user_lifetime',
            dashboardItem: {
                title: "Average users's lifetime",
                chartDocumentation: MopaAbtestsChartDocs.avg_user_lifetime,
            },
            xColumnLabel: 'day_no',
            yColumnLabel: 'avg_user_lifetime',
            metricName: "Avg users's lifetime",
            echartsSeriesType: 'line',
            xAxisType: 'value',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            tooltip: {
                xLabelFormatterGetter: () => (value: string) =>
                    `Day no: ${value}`,
                valueFormatterGetter: () => (v: any) =>
                    EChartsTooltipHelper.tooltipNumberValueFormatter(
                        v,
                        ' days'
                    ),
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.abtests.retention_by_day_no'].emitter,
    };

    avg_user_lifetime_uplift: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.retention.chart.avg_user_lifetime_uplift',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId: 'mopa.retention.chart.avg_user_lifetime_uplift',
            dashboardItem: {
                title: "Average users's lifetime uplift",
                chartDocumentation:
                    MopaAbtestsChartDocs.avg_user_lifetime_uplift,
            },
            xColumnLabel: 'day_no',
            yColumnLabel: 'avg_user_lifetime_uplift',
            metricName: "Avg users's lifetime uplift",
            echartsSeriesType: 'line',
            xAxisType: 'value',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            tooltip: {
                xLabelFormatterGetter: () => (value: string) =>
                    `Day no: ${value}`,
                valueFormatterGetter: () =>
                    EChartsTooltipHelper.tooltipPercentValueFormatter,
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.getAxisPercentLabelFormatter(0),
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.abtests.retention_by_day_no'].emitter,
    };

    med_sess_length_by_day_no: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.engagement.chart.med_sess_length_by_day_no',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.engagement.chart.med_sess_length_by_day_no',
            dashboardItem: {
                title: 'Median session lenght per user',
                chartDocumentation:
                    MopaAbtestsChartDocs.med_sess_length_by_day_no,
            },
            xColumnLabel: 'day_no',
            yColumnLabel: 'median_sess_length_mins',
            metricName: 'Median session length',
            echartsSeriesType: 'line',
            xAxisType: 'value',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            getEchartsOptionsOverride: (context: IQueryContext) => {
                return {
                    // legend: { show: false },
                };
            },
            tooltip: {
                xLabelFormatterGetter: () => (value: string) =>
                    `Day no: ${value}`,
                valueFormatterGetter: () => {
                    return (v) =>
                        EChartsTooltipHelper.tooltipNumberValueFormatter(
                            v,
                            ' min'
                        );
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.abtests.sessions_by_day_no'].emitter,
    };

    // uplift
    med_sess_length_by_day_no_uplift: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.engagement.chart.med_sess_length_by_day_no_uplift',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.engagement.chart.med_sess_length_by_day_no_uplift',
            dashboardItem: {
                title: 'Median session lenght per user by day number uplift',
                chartDocumentation:
                    MopaAbtestsChartDocs.med_sess_length_by_day_no_uplift,
            },
            xColumnLabel: 'day_no',
            yColumnLabel: 'median_sess_length_mins_uplift',
            metricName: 'Median session length uplift',
            echartsSeriesType: 'line',
            xAxisType: 'value',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            getEchartsOptionsOverride: (context: IQueryContext) => {
                return {
                    // legend: { show: false },
                };
            },
            tooltip: {
                xLabelFormatterGetter: () => (value: string) =>
                    `Day no: ${value}`,
                valueFormatterGetter: () =>
                    EChartsTooltipHelper.tooltipPercentValueFormatter,
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.getAxisPercentLabelFormatter(0),
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.abtests.sessions_by_day_no'].emitter,
    };

    avg_no_sessions_by_day_no: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.engagement.chart.avg_no_sessions_by_day_no',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.engagement.chart.avg_no_sessions_by_day_no',
            dashboardItem: {
                title: 'Number of sessions per user by day number',
                chartDocumentation:
                    MopaAbtestsChartDocs.avg_no_sessions_by_day_no,
            },
            xColumnLabel: 'day_no',
            yColumnLabel: 'avg_sess_per_user',
            metricName: 'Avg sessions per user',
            echartsSeriesType: 'line',
            xAxisType: 'value',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            getEchartsOptionsOverride: (context: IQueryContext) => {
                return {
                    legend: { show: false },
                };
            },
            tooltip: {
                xLabelFormatterGetter: () => (value: string) =>
                    `Day no: ${value}`,
                valueFormatterGetter: () =>
                    EChartsTooltipHelper.tooltipNumberValueFormatter,
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.abtests.sessions_by_day_no'].emitter,
    };

    // uplift
    avg_no_sessions_by_day_no_uplift: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.engagement.chart.avg_no_sessions_by_day_no_uplift',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.engagement.chart.avg_no_sessions_by_day_no_uplift',
            dashboardItem: {
                title: 'Number of sessions per user by day number uplift',
                chartDocumentation:
                    MopaAbtestsChartDocs.avg_no_sessions_by_day_no_uplift,
            },
            xColumnLabel: 'day_no',
            yColumnLabel: 'avg_sess_per_user_uplift',
            metricName: 'Avg sessions per user uplift',
            echartsSeriesType: 'line',
            xAxisType: 'value',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            getEchartsOptionsOverride: (context: IQueryContext) => {
                return {
                    legend: { show: false },
                };
            },
            tooltip: {
                xLabelFormatterGetter: () => (value: string) =>
                    `Day no: ${value}`,
                valueFormatterGetter: () =>
                    EChartsTooltipHelper.tooltipPercentValueFormatter,
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.getAxisPercentLabelFormatter(0),
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.abtests.sessions_by_day_no'].emitter,
    };

    // running median progress
    running_median_progress: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.abtests.running_median_progress',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId: 'mopa.abtests.chart.running_median_progress',
            dashboardItem: {
                title: 'Running median total progress',
                chartDocumentation:
                    MopaAbtestsChartDocs.running_median_progress,
            },
            xColumnLabel: 'day_no',
            yColumnLabel: 'median_progress_sum_total',
            metricName: 'Running median progress',
            echartsSeriesType: 'line',
            xAxisType: 'value',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            tooltip: {
                xLabelFormatterGetter: () => (value: string) =>
                    `Day no: ${value}`,
                valueFormatterGetter: () =>
                    EChartsTooltipHelper.tooltipNumberValueFormatter,
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.abtests.progress_sessions'].emitter,
    };

    // running median progress_uplift
    running_median_progress_uplift: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.abtests.running_median_progress_uplift',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.abtests.chart.running_median_progress_uplift',
            dashboardItem: {
                title: 'Running median total progress uplift',
                chartDocumentation:
                    MopaAbtestsChartDocs.running_median_progress_uplift,
            },
            xColumnLabel: 'day_no',
            yColumnLabel: 'median_progress_sum_total_uplift',
            metricName: 'Running median progress upflift',
            echartsSeriesType: 'line',
            xAxisType: 'value',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            tooltip: {
                xLabelFormatterGetter: () => (value: string) =>
                    `Day no: ${value}`,
                valueFormatterGetter: () =>
                    EChartsTooltipHelper.tooltipPercentValueFormatter,
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.getAxisPercentLabelFormatter(0),
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.abtests.progress_sessions'].emitter,
    };

    // running median playthrough length
    running_median_playthrough_length: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.abtests.running_median_playthrough_length',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.abtests.chart.running_median_playthrough_length',
            dashboardItem: {
                title: 'Running median playthrough length',
                chartDocumentation:
                    MopaAbtestsChartDocs.running_median_playthrough_length,
            },
            xColumnLabel: 'day_no',
            yColumnLabel: 'running_median_duration_min',
            metricName: 'Running median playthrough length',
            echartsSeriesType: 'line',
            xAxisType: 'value',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            tooltip: {
                xLabelFormatterGetter: () => (value: string) =>
                    `Day no: ${value}`,
                valueFormatterGetter: () => {
                    return (v) =>
                        EChartsTooltipHelper.tooltipNumberValueFormatter(
                            v,
                            ' min'
                        );
                },
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.abtests.progress_sessions'].emitter,
    };

    // running median playthrough length uplift
    running_median_playthrough_length_uplift: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.abtests.running_median_playthrough_length_uplift',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.abtests.chart.running_median_playthrough_length_uplift',
            dashboardItem: {
                title: 'Running median playthrough length uplift',
                chartDocumentation:
                    MopaAbtestsChartDocs.running_median_playthrough_length_uplift,
            },
            xColumnLabel: 'day_no',
            yColumnLabel: 'running_median_duration_min_uplift',
            metricName: 'Running median playthrough length uplift',
            echartsSeriesType: 'line',
            xAxisType: 'value',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            tooltip: {
                xLabelFormatterGetter: () => (value: string) =>
                    `Day no: ${value}`,
                valueFormatterGetter: () =>
                    EChartsTooltipHelper.tooltipPercentValueFormatter,
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.getAxisPercentLabelFormatter(0),
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.abtests.progress_sessions'].emitter,
    };

    revenue: IDashboardItemDataLoaderLink = {
        id: 'mopa.abtests.chart.revenue',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            createdInstanceId: 'mopa.abtests.chart.revenue',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            dashboardItem: {
                title: 'Cohort revenue',
                chartDocumentation: MopaAbtestsChartDocs.revenue,
            },
            dashboardName: this.DASHBOARD_NAME,
            xColumnLabel: 'date',
            metricsGetter: EChartsHelper.createRevenueMetricsGetter({
                metricId: 'revenue',
                metricName: 'Cohort revenue',
            }),
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter: (context: IQueryContext) => {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter: (context: IQueryContext) => {
                    return (v: any) =>
                        EChartsTooltipHelper.tooltipCurrencyValueFormatter(
                            v,
                            context.settings.currency || 'pln'
                        );
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.abtests.revenue_agg_dau'].emitter,
    };

    running_revenue_by_date: IDashboardItemDataLoaderLink = {
        id: 'mopa.abtests.chart.running_revenue_by_date',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            createdInstanceId: 'mopa.abtests.chart.running_revenue_by_date',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            dashboardItem: {
                title: 'Running cohort revenue by date',
                chartDocumentation:
                    MopaAbtestsChartDocs.running_revenue_by_date,
            },
            dashboardName: this.DASHBOARD_NAME,
            xColumnLabel: 'date',
            metricsGetter: EChartsHelper.createRevenueMetricsGetter({
                metricId: 'running_revenue',
                metricName: 'Running cohort revenue',
            }),
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter: (context: IQueryContext) => {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter: (context: IQueryContext) => {
                    return (v: any) =>
                        EChartsTooltipHelper.tooltipCurrencyValueFormatter(
                            v,
                            context.settings.currency || 'pln'
                        );
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.abtests.revenue_agg_dau'].emitter,
    };

    dau_calendary: IDashboardItemDataLoaderLink = {
        id: 'mopa.abtests.chart.dau_calendary',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            createdInstanceId: 'mopa.abtests.chart.dau_calendary',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            dashboardItem: {
                title: 'DAU',
                chartDocumentation: MopaAbtestsChartDocs.dau_calendary,
            },
            dashboardName: this.DASHBOARD_NAME,
            xColumnLabel: 'date',
            yColumnLabel: 'sum_daily_active_users',
            metricName: 'DAU',
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter: (context: IQueryContext) => {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter: () => (v: any) =>
                    EChartsTooltipHelper.tooltipNumberValueFormatter(
                        v,
                        undefined,
                        0
                    ),
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.abtests.revenue_agg_dau'].emitter,
    };

    dau_by_day_no: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.abtests.dau_by_day_no',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId: 'mopa.abtests.chart.dau_by_day_no',
            dashboardItem: {
                title: 'DAU',
                chartDocumentation: MopaAbtestsChartDocs.dau_by_day_no,
            },
            xColumnLabel: 'day_no',
            yColumnLabel: 'players',
            metricName: 'DAU',
            echartsSeriesType: 'line',
            xAxisType: 'value',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            tooltip: {
                xLabelFormatterGetter: () => (value: string) =>
                    `Day no: ${value}`,
                valueFormatterGetter: () => (v: any) =>
                    EChartsTooltipHelper.tooltipNumberValueFormatter(
                        v,
                        undefined,
                        0
                    ),
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.abtests.progress_sessions'].emitter,
    };

    dau_by_day_no_uplift: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.abtests.dau_by_day_no_uplift',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId: 'mopa.abtests.chart.dau_by_day_no_uplift',
            dashboardItem: {
                title: 'DAU uplift',
                chartDocumentation: MopaAbtestsChartDocs.dau_by_day_no_uplift,
            },
            xColumnLabel: 'day_no',
            yColumnLabel: 'players_uplift',
            metricName: 'DAU uplift',
            echartsSeriesType: 'line',
            xAxisType: 'value',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            tooltip: {
                xLabelFormatterGetter: () => (value: string) =>
                    `Day no: ${value}`,
                valueFormatterGetter: () =>
                    EChartsTooltipHelper.tooltipPercentValueFormatter,
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.getAxisPercentLabelFormatter(0),
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.abtests.progress_sessions'].emitter,
    };

    override async ngOnInit() {
        super.ngOnInit();
        console.log({
            msg: 'MopaAbtestsComponent.ngOnInit',
            cancellationToken: this.cancellationToken,
        });
    }

    async ngAfterViewInit() {
        console.log({
            msg: 'MopaAbtestsComponent.ngAfterViewInit',
            cancellationToken: this.cancellationToken,
        });

        this.standardDashboardInitialize(this.dashboardInitOptions);
    }

    override getInitialContext(): IQueryContext {
        const initialContext = QueryContextService.INITIAL_CONTEXT;

        // set initialContext for AppStore to Google Play
        initialContext.datasourceColumns = [];
        // add settings
        initialContext.settings[QueryContextSettingID.ABTESTS_MAX_DAY_NO] =
            QueryContextDefaultSettings[
                QueryContextSettingID.ABTESTS_MAX_DAY_NO
            ];
        // split group default value
        // NOTE: The key survives structuredClone and breaks
        // Firestore addDoc on saved contexts.
        // initialContext.settings[
        //     QueryContextSettingID.ABTESTS_UPLIFT_REFERENCE
        // ] = undefined;

        return initialContext;
    }

    static cohortOverviewTableColumnsGetter(
        context: IQueryContext
    ): TableColumnItem[] {
        return [
            {
                id: 'cohort_size',
                name: 'Cohort size',
                nzLeft: false,
                // nzAlign: 'center',
                nzSortFn: TableDashboardItemHelper.defaultSortFnGetter(
                    'cohort_size',
                    'number'
                ),
                // nzWidth: '10%',
            },
            {
                id: 'revenue_net_pln',
                name: 'Revenue net pln',
                nzLeft: false,
                // nzAlign: 'center',
                nzSortFn: TableDashboardItemHelper.defaultSortFnGetter(
                    'revenue_net_pln',
                    'number'
                ),
                labelFormatter: (value: any) => {
                    return EChartsTooltipHelper.tooltipCurrencyValueFormatter(
                        value,
                        context!.settings.currency || 'pln'
                    );
                },
                // nzWidth: '90%',
            },
        ];
    }

    /**
     * Select first view from dashboardViews
     *
     **/
    static getDefaultABTestDashboardView(dashboardViews: DashboardView[]) {
        return dashboardViews[0];
    }
}
